<script setup lang="ts">
import {Dialog, DialogScrollContent} from "@/core/components/ui/dialog";
import {NotificationTemplate} from "@/notification-template/interfaces";
import PhoneInputStep from "@/rcs/components/try-dialog/steps/PhoneInputStep.vue";
import {computed, ref, watch} from "vue";
import NotEnoughCreditStep from "@/rcs/components/try-dialog/steps/NotEnoughCreditStep.vue";
import {storeToRefs} from "pinia";
import {useCreditStore} from "@/stores/credit.store";
import SendSuccessStep from "@/rcs/components/try-dialog/steps/SendSuccessStep.vue";
import SendErrorStep from "@/rcs/components/try-dialog/steps/SendErrorStep.vue";
import {notificationTemplateApi} from "@/container";

const selectedTemplate = defineModel<NotificationTemplate | undefined>();

const phone = ref('');
const creditStore = useCreditStore();
const {credit} = storeToRefs(creditStore);
const sending = ref(false);

const step = ref<'phone' | 'credit-refill' | 'success' | 'error'>('phone');

watch(() => selectedTemplate.value, (newVal) => {
    if (newVal) {
        step.value = 'phone';
    }
});

const handleOpenChange = (value: boolean) => {
    if (!value) {
        selectedTemplate.value = undefined;
    }
};

const cancel = () => {
    selectedTemplate.value = undefined;
};

const formattedPhone = computed(() => {
    if (!phone.value) {
        return '';
    }

    const digits = phone.value.replace(/\s/g, '');

    const countryCode = digits.startsWith('+33') ? '+33 ' : '';
    const rest = digits.replace(/^\+33/, '').replace(/^0/, '');

    return `${countryCode} ${rest.replace(/(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}`;
});

const send = (_phone: string) => {
    if (!selectedTemplate.value) {
        return;
    }

    if (!credit.value?.balance) {
        step.value = 'credit-refill';
        return;
    }

    const cost = selectedTemplate.value.credits;
    const balance = credit.value.balance;

    if (cost > balance) {
        step.value = 'credit-refill';
        return;
    }

    phone.value = _phone;
    sending.value = true;

    return notificationTemplateApi()
        .tryTemplate(selectedTemplate.value!.id, phone.value)
        .then(({success}) => {
            step.value = success ? 'success' : 'error';
            creditStore.refresh();
        })
        .catch(() => {
            step.value = 'error';
        })
        .finally(() => sending.value = false);
};
</script>

<template>
  <Dialog :open="!!selectedTemplate" @update:open="handleOpenChange">
    <DialogScrollContent v-if="selectedTemplate" class="max-w-2xl">
      <PhoneInputStep
        v-if="step === 'phone'"
        v-model:phone="phone"
        :template="selectedTemplate"
        :sending="sending"
        @cancel="cancel"
        @send="(_phone) => send(_phone)"
      />
      <NotEnoughCreditStep v-if="step === 'credit-refill'" :template="selectedTemplate" @cancel="cancel" />
      <SendSuccessStep v-if="step === 'success'" :formatted="formattedPhone" @modify="step = 'phone'" @finish="cancel" />
      <SendErrorStep
        v-if="step === 'error'" :formatted="formattedPhone" :retrying="sending" @close="cancel"
        @retry="send(phone)"
      />
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>

</style>
